import dayjs from 'dayjs'
import queryString from 'query-string'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactApexChart from 'react-apexcharts'
import { sumBy } from 'lodash'

import { AbilityContext, isAdmin, isMerchant } from '../../components/Ability'
import Helmet from '../../components/Helmet'
import SummaryCard from './Features/SummaryCard'
import useAxios from '../../hooks/useAxios'
import {
  ICostDailyReport,
  IMerchantDailyReport,
} from '../Report/Features/DailySalesReport'
import { ONLY_DATE_FORMAT } from '../../constants/global'
import { mergeObjects, numberFormat } from '../../utils'
import { ContentWrapper } from '../../components/ContentWrapper'
import {
  FcApproval,
  FcBusinessman,
  FcCellPhone,
  FcCurrencyExchange,
  FcDonate,
  FcHeadset,
  FcMoneyTransfer,
  FcPaid,
  FcPhoneAndroid,
  FcUndo,
} from 'react-icons/fc'
import { Card, CardHeader, Grid, MenuItem, TextField } from '@mui/material'
import HeaderContent from '../../components/Layouts/HeaderContent'
import DateRangePicker from '../../components/DateRangePicker'
import PageUrls from '../../constants/page-urls'
import { PaginationResponseInterface } from '../../types/pagination'
import {
  IOrder,
  IOrderProductTop10,
  IOrderProvincePopular,
  OrderSummary,
} from '../../types/order'
import Button from '@mui/material/Button'
import SearchIcon from '@mui/icons-material/Search'
import { useUser } from '../../hooks/useUser'
import { UserRole } from '../../types/user'
import { IUserDailyReport, IUserOrderCancelledReport } from '../../types/report'

interface IReport extends IMerchantDailyReport {
  costAmountTotal: number
}

const DashboardPage = () => {
  const navigate = useNavigate()
  const user = useUser()
  const merchant = user.merchant
  const showAllCard = isAdmin(user.user as any) || isMerchant(user.user as any)

  const ability = useContext(AbilityContext)
  const queryStr = queryString.parse(window.location.search)
  const [date, setDate] = useState<{
    startDate: string | any
    endDate: string | any
  }>({
    startDate: dayjs().format(),
    endDate: dayjs().format(),
  })
  const [searchDate, setSearchDate] = useState<{
    startDate: string | any
    endDate: string | any
  }>({
    startDate: queryStr.startDate ? String(queryStr.startDate) : date.startDate,
    endDate: queryStr.endDate ? String(queryStr.endDate) : date.endDate,
  })
  const [commissionSelected, setCommissionSelected] = useState<
    string | 'commission' | 'commissionOrder' | 'commissionPercent'
  >('commission')

  const handleSearch = () => {
    const newQuery = queryString.stringify({ ...date })
    setSearchDate(date)
    navigate(`${PageUrls.Home}?${newQuery}`, {
      replace: true,
    })
  }

  const handleSetDate = (newFilter: {
    startDate: string | any
    endDate: string | any
  }) => {
    setDate(newFilter)
  }

  useEffect(() => {
    handleSetDate(date)
  }, [])

  // const ordersList = useAxios<
  //   PaginationResponseInterface<IOrder> & { total: OrderSummary }
  // >({
  //   method: 'GET',
  //   url: '/order',
  //   params: { ...searchDate, page: 1, perPage: 1000, showTotal: '1' },
  // })

  const productsTop10 = useAxios<IOrderProductTop10[]>({
    method: 'GET',
    url: '/order/products/top10',
    params: {
      ...searchDate,
    },
  })

  const provincesList = useAxios<IOrderProvincePopular[]>({
    method: 'GET',
    url: '/order/provinces/popular',
    params: {
      ...searchDate,
    },
  })

  const merchantDailyReports = useAxios<[IMerchantDailyReport]>({
    method: 'POST',
    url: '/report/merchant-daily-reports',
    data: {
      ...searchDate,
    },
  })

  const userCommissionReports = useAxios<IUserDailyReport[]>({
    method: 'GET',
    url: '/report/user-daily-reports',
    params: {
      ...searchDate,
      type: 'order',
    },
  })

  const userCancelledReport = useAxios<IUserOrderCancelledReport[]>({
    method: 'GET',
    url: '/report/order-cancelled-reports',
    params: {
      ...searchDate,
    },
  })

  const merchantDailyReturnReports = useAxios<[IMerchantDailyReport]>({
    method: 'POST',
    url: '/report/merchant-daily-reports',
    data: {
      ...searchDate,
      spacificStatus: ['returned'],
    },
  })

  const costDailyReport = useAxios<[ICostDailyReport]>({
    method: 'GET',
    url: '/report/cost-daily-reports',
    params: {
      ...searchDate,
    },
  })

  const costObject = costDailyReport?.data?.reduce(
    (acc: any, costDailyReportItem: ICostDailyReport) => {
      const date = dayjs(costDailyReportItem.date).format(ONLY_DATE_FORMAT)
      acc[date] = {
        costAmountTotal: costDailyReportItem.amount,
      }
      return acc
    },
    {},
  )

  const merchantObject = merchantDailyReports?.data?.reduce(
    (acc: any, merchantDailyReportItem: IMerchantDailyReport) => {
      const date = dayjs(merchantDailyReportItem.date).format(ONLY_DATE_FORMAT)
      acc[date] = merchantDailyReportItem
      return acc
    },
    {},
  )

  const merchantsData = merchantDailyReports?.data
  const userCommissionReportsData = userCommissionReports?.data || []

  const takeComission = sumBy(userCommissionReportsData, 'value.commission')
  const takeComissionOrder = sumBy(
    userCommissionReportsData,
    'value.commissionOrder',
  )
  const takeComissionPercent = sumBy(
    userCommissionReportsData,
    'value.commissionPercent',
  )

  const cancelComission = sumBy(userCancelledReport.data, 'commission')
  const cancelComissionOrder = sumBy(
    userCancelledReport.data,
    'commissionOrder',
  )
  const cancelComissionPercent = sumBy(
    userCancelledReport.data,
    'commissionPercent',
  )

  // console.log('comission', takeComission, cancelComission)

  const merchantReport = {
    bankCount: sumBy(merchantsData, 'bankCount'),
    bankTotal: sumBy(merchantsData, 'bankTotal'),
    codCount: sumBy(merchantsData, 'codCount'),
    commission: takeComission - cancelComission,
    commissionOrder: takeComissionOrder - cancelComissionOrder,
    commissionPercent: takeComissionPercent - cancelComissionPercent,
    commissionCancel: cancelComission,
    commissionOrderCancel: cancelComissionOrder,
    commissionPercentCancel: cancelComissionPercent,
    costTotal: sumBy(merchantsData, 'costTotal'),
    count: sumBy(merchantsData, 'count'),
    crmCount: sumBy(merchantsData, 'crmCount'),
    crmTotal: sumBy(merchantsData, 'crmTotal'),
    discount: sumBy(merchantsData, 'discount'),
    hasTracking: sumBy(merchantsData, 'hasTracking'),
    noTracking: sumBy(merchantsData, 'noTracking'),
    subTotal: sumBy(merchantsData, 'subTotal'),
    total: sumBy(merchantsData, 'total'),
    unit: sumBy(merchantsData, 'unit'),
    telesaleTotal: sumBy(merchantsData, 'telesaleTotal'),
    upsaleTotal: sumBy(merchantsData, 'upsaleTotal'),
    upsalePriceTotal: sumBy(merchantsData, 'upsalePriceTotal'),
    unsuccessUpSaleTotal: sumBy(merchantsData, 'unsuccessUpSaleTotal'),
  }

  const merchantsReturnData = merchantDailyReturnReports?.data

  const merchantReturnReport = {
    bankCount: sumBy(merchantsReturnData, 'bankCount'),
    bankTotal: sumBy(merchantsReturnData, 'bankTotal'),
    codCount: sumBy(merchantsReturnData, 'codCount'),
    commission: sumBy(merchantsReturnData, 'commission'),
    commissionOrder: sumBy(merchantsReturnData, 'commissionOrder'),
    commissionPercent: sumBy(merchantsReturnData, 'commissionPercent'),
    costTotal: sumBy(merchantsReturnData, 'costTotal'),
    count: sumBy(merchantsReturnData, 'count'),
    crmCount: sumBy(merchantsReturnData, 'crmCount'),
    crmTotal: sumBy(merchantsReturnData, 'crmTotal'),
    discount: sumBy(merchantsReturnData, 'discount'),
    hasTracking: sumBy(merchantsReturnData, 'hasTracking'),
    noTracking: sumBy(merchantsReturnData, 'noTracking'),
    subTotal: sumBy(merchantsReturnData, 'subTotal'),
    total: sumBy(merchantsReturnData, 'total'),
    unit: sumBy(merchantsReturnData, 'unit'),
    telesaleTotal: sumBy(merchantsData, 'telesaleTotal'),
    upsaleTotal: sumBy(merchantsData, 'upsaleTotal'),
    upsalePriceTotal: sumBy(merchantsData, 'upsalePriceTotal'),
    unsuccessUpSaleTotal: sumBy(merchantsData, 'unsuccessUpSaleTotal'),
  }

  const costsData = costDailyReport?.data
  const costReport = {
    cost: sumBy(costsData, 'amount'),
  }

  const dashboardData = {
    ...merchantReport,
    ...costReport,
    cost: costReport.cost + merchantReport.costTotal,
    net: merchantReport.total - (costReport.cost + merchantReport.costTotal),
    returnTotal: merchantReturnReport.total,
  }

  const commissions: Record<string, any> = {
    commission: dashboardData.commission,
    commissionOrder: dashboardData.commissionOrder,
    commissionPercent: dashboardData.commissionPercent,
  }

  const reports = mergeObjects(costObject, merchantObject)

  // console.log('reports', reports)

  const reportsArray: IReport[] = Object.keys(reports).map(
    (date: string, index: number) => {
      const merchantDailyReportItem = reports[date]
      return {
        ...merchantDailyReportItem,
        costAmountTotal:
          (merchantDailyReportItem?.costTotal || 0) +
          (merchantDailyReportItem?.costAmountTotal || 0),
        key: index,
      }
    },
  )

  return (
    <ContentWrapper>
      <Helmet title="รายงานภาพรวม" />
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <Grid item>
          <HeaderContent goBack title="ภาพรวมระบบ" descriptions="ภาพรวมระบบ" />
        </Grid>
        <Grid item>
          <Grid container spacing={1} alignItems={'center'}>
            <Grid item>
              <TextField
                sx={{ minWidth: 200 }}
                fullWidth
                label="เลือกประเภทค่าคอม"
                select
                onChange={(e) => {
                  setCommissionSelected(e.target.value)
                }}
              >
                <MenuItem value={'commission'}>ค่าคอมต่อชิ้น</MenuItem>
                <MenuItem value={'commissionOrder'}>
                  ค่าคอมต่อคำสั่งซื้อ
                </MenuItem>
                <MenuItem value={'commissionPercent'}>ค่าคอม %</MenuItem>
              </TextField>
            </Grid>
            <Grid item>
              <DateRangePicker
                startDate={date.startDate}
                endDate={date.endDate}
                onChange={(startDate, endDate) => {
                  handleSetDate({ startDate, endDate })
                }}
              />
            </Grid>
            <Grid item>
              <Button
                color="primary"
                size="large"
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => {
                  handleSearch()
                }}
              >
                ค้นหา
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={12}>
          <SummaryCard
            data={[
              {
                today: 'ยอดขาย',
                title: `฿${numberFormat(dashboardData.total ?? 0)} บาท`,
                persent: '+30%',
                icon: <FcCurrencyExchange size={50} />,
                bnb: 'bnb2',
                isShow: showAllCard || merchant.config?.display?.sales,
              },
              {
                today: 'รายจ่าย',
                title: `฿${numberFormat(dashboardData.cost ?? 0)} บาท`,
                persent: '+20%',
                icon: <FcDonate size={50} />,
                bnb: 'bnb2',
                isShow: showAllCard || merchant.config?.display?.cost,
              },
              {
                today: 'กำไร',
                title: `฿${numberFormat(dashboardData.net ?? 0)} บาท`,
                persent: '-20%',
                icon: <FcApproval size={50} />,
                bnb: 'redtext',
                isShow: showAllCard || merchant.config?.display?.profit,
              },
              {
                today: 'จำนวนชิ้นที่ขายได้',
                title: `${numberFormat(dashboardData.unit ?? 0, 0)} ชิ้น`,
                persent: '10%',
                icon: <FcApproval size={50} />,
                bnb: 'bnb2',
                isShow: showAllCard || merchant.config?.display?.unit,
              },
              {
                today: 'ยอดตีกลับ',
                title: `฿${numberFormat(dashboardData.returnTotal ?? 0)} บาท`,
                persent: '10%',
                icon: <FcUndo size={50} />,
                bnb: 'bnb2',
                isShow: showAllCard || merchant.config?.display?.return,
              },
              {
                today: 'ยอด CRM',
                title: `฿${numberFormat(dashboardData.crmTotal ?? 0)} บาท`,
                persent: '10%',
                icon: <FcHeadset size={50} />,
                bnb: 'bnb2',
                isShow:
                  user.merchant?.config?.openCRM &&
                  (merchant.config?.display?.crm || showAllCard),
              },
              {
                today: 'จำนวนคำสั่งซื้อ',
                title: `${numberFormat(dashboardData.count ?? 0)} ออร์เดอร์`,
                persent: '10%',
                icon: <FcPaid size={50} />,
                bnb: 'bnb2',
                isShow: showAllCard || merchant.config?.display?.order,
              },
              {
                today: 'ค่าคอมมิชชั่น',
                title: `฿${
                  commissions?.[
                    commissionSelected ?? 'commission'
                  ]?.toLocaleString() ?? 0
                } บาท`,
                persent: '10%',
                icon: <FcBusinessman size={50} />,
                bnb: 'commission',
                isShow:
                  showAllCard ||
                  Boolean(user.merchant?.config?.display?.comission) ||
                  [UserRole.ADMIN, UserRole.MERCHANT, UserRole.HEAD].some(
                    (role) => (user.user.roles as UserRole[])?.includes(role),
                  ),
              },
              {
                today: 'จำนวนออร์เดอร์ telesale',
                title: `${numberFormat(
                  dashboardData.telesaleTotal ?? 0,
                )} ออร์เดอร์`,
                persent: '10%',
                icon: <FcPhoneAndroid size={50} />,
                bnb: 'bnb2',
                isShow: showAllCard || merchant.config?.display?.telesaleTotal,
              },
              {
                today: 'จำนวนออร์เดอร์ที่อัพเซลได้',
                title: `${numberFormat(dashboardData.upsaleTotal ?? 0)} ชิ้น`,
                persent: '10%',
                icon: <FcCellPhone size={50} />,
                bnb: 'bnb2',
                isShow: showAllCard || merchant.config?.display?.upsaleTotal,
              },
              {
                today: 'ยอด telesale',
                title: `฿${numberFormat(
                  dashboardData.upsalePriceTotal ?? 0,
                )} บาท`,
                persent: '10%',
                icon: <FcMoneyTransfer size={50} />,
                bnb: 'bnb2',
                isShow:
                  showAllCard || merchant.config?.display?.upsalePriceTotal,
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={12} lg={12}>
          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CardHeader
              title="รายงานสรุปยอดการขาย"
              subheader="รายงานสรุปยอดการขาย"
            />
            <div id="chart" style={{ width: '100%' }}>
              <ReactApexChart
                options={{
                  chart: {
                    id: 'basic-bar',
                  },
                  xaxis: {
                    categories:
                      reportsArray.map((report) =>
                        dayjs(report.date).format('DD MMM YYYY'),
                      ) || [],
                  },
                  yaxis: {
                    title: {
                      text: '฿(บาท)',
                    },
                  },
                }}
                series={[
                  {
                    name: 'COD',
                    data:
                      reportsArray?.map((report) => report.codTotal || 0) || [],
                  },
                  {
                    name: 'โอน',
                    data:
                      reportsArray?.map((report) => report.bankTotal || 0) ||
                      [],
                  },
                  {
                    name: 'รวม',
                    data:
                      reportsArray?.map((report) => report.total || 0) || [],
                  },
                  {
                    name: 'รายจ่าย',
                    data:
                      reportsArray?.map(
                        (report) => report.costAmountTotal || 0,
                      ) || [],
                  },
                ]}
                type="bar"
                height={350}
              />
            </div>
          </Card>
        </Grid>
        {productsTop10?.data?.length! > 0 && provincesList.data?.length && (
          <>
            <Grid item xs={12} md={12} lg={6}>
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CardHeader
                  title="รายการสินค้าขายดี 10 อันดับแรก"
                  subheader="รายการสินค้าขายดี 10 อันดับแรก"
                />
                <div id="chart" style={{ width: '100%' }}>
                  <ReactApexChart
                    options={{
                      labels:
                        productsTop10?.data?.map(
                          (product) =>
                            `${product.productName} (${numberFormat(
                              product.quantity,
                            )} - ${numberFormat(product.total)})`,
                        ) || [],
                    }}
                    series={
                      productsTop10?.data?.map((product) => product.total) || []
                    }
                    type="donut"
                  />
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} md={12} lg={6}>
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CardHeader
                  title="รายการ 10 อันดับแรกของจังหวัดที่สั่งซื้อมากที่สุด"
                  subheader="รายการ 10 อันดับแรกของจังหวัดที่สั่งซื้อมากที่สุด"
                />
                <div id="chart" style={{ width: '100%' }}>
                  <ReactApexChart
                    options={{
                      labels:
                        provincesList.data?.map(
                          (province) =>
                            `${province.name} (${numberFormat(
                              province.count,
                            )})`,
                        ) || [],
                    }}
                    series={
                      provincesList.data?.map((province) => province.count) ||
                      []
                    }
                    type="pie"
                  />
                </div>
              </Card>
            </Grid>
          </>
        )}
      </Grid>
    </ContentWrapper>
  )
}

export default DashboardPage
